// Mirrors PSW_SKILL_NAMES in staffyapi/server/components/gta-detection.js —
// the one pair of skills a shift can currently be broadcast to jointly.
export const PSW_PCA_PAIR = ['personal support worker', 'personal care aide'];

const PSW_PCA_SHORT_LABEL: { [key: string]: string } = {
  'personal support worker': 'PSW',
  'personal care aide': 'PCA',
};

export function skillShortLabel(skillName: string): string {
  return PSW_PCA_SHORT_LABEL[(skillName || '').toLowerCase()] || skillName || '';
}

export function isPswPcaSkill(skill: string): boolean {
  return PSW_PCA_PAIR.includes((skill || '').toLowerCase());
}

// Worker-app display label for a job's skill: when the shift accepts more
// than one distinct skill (job.skill + job.acceptableSkills), show the short
// combined form (e.g. "PSW/PCA"); otherwise null, so the caller falls back to
// its normal single-skill translated label.
export function combinedSkillShortLabel(jobItem: { skill?: string; acceptableSkills?: string[] }): string | null {
  const acceptableSkills = Array.isArray(jobItem?.acceptableSkills) ? jobItem.acceptableSkills : [];
  const distinctSkills = Array.from(new Set([jobItem?.skill, ...acceptableSkills].filter(skill => !!skill)));
  return distinctSkills.length > 1 ? distinctSkills.map(skillShortLabel).join('/') : null;
}

// The shift-level "also accepts" skill — the acceptableSkills entry that
// isn't the shift's own primary skill. Null when the shift doesn't have a
// PSW/PCA-style pairing set.
export function getShiftAlsoAcceptsSkill(job: { skill?: string; acceptableSkills?: string[] }): string | null {
  if (!job?.acceptableSkills?.length) return null;
  const primary = (job.skill || '').toLowerCase();
  const also = job.acceptableSkills
    .map(s => (s || '').toLowerCase())
    .find(s => s && s !== primary);
  return also || null;
}

// Which acceptable skill a given applicant actually qualified under, when
// that differs from the shift's primary skill (e.g. a PCA-holding applicant
// on a PSW shift that also accepts PCA). Null when they hold the primary
// skill, or the shift has no acceptableSkills pairing.
export function getApplicantQualifyingOtherSkill(
  staffer: { skills?: Array<{ name?: string; has?: boolean }> },
  job: { skill?: string; acceptableSkills?: string[] },
): string | null {
  const alsoAccepts = getShiftAlsoAcceptsSkill(job);
  if (!alsoAccepts) return null;

  const primary = (job.skill || '').toLowerCase();
  const heldSkillNames = (staffer?.skills || [])
    .filter(skill => skill?.has)
    .map(skill => (skill?.name || '').toLowerCase());

  if (heldSkillNames.includes(primary)) return null;
  return heldSkillNames.includes(alsoAccepts) ? alsoAccepts : null;
}
